import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  DEFAULT_COMP_SPREAD_BEAT_RATE_BY,
  DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES,
  DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { shouldTriggerRateRefresh } from "@redotech/redo-fulfillment-outbound-labels-common/should-trigger-rate-refresh";
import { TransferDistributionMode } from "@redotech/redo-fulfillment-wms-common/wms-constants-enums";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { ChannelConnectionState } from "@redotech/redo-web/arbiter-components/settings-card/settings-card";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../app/auth";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../client/context";
import { getTeam, updateTeam } from "../../client/team";
import { notifyAdminRateRefreshNeeded } from "../../rate-refresh";
import { LoadTeamContext, TeamContext } from "../team";
import * as teamCss from "../team.module.css";
import { AddBalanceCreditCard } from "./outbound-labels/add-balance-credit";
import {
  BalanceSettings,
  balanceSettingsDefaultValue,
  balanceSettingsForm,
} from "./outbound-labels/balance-settings";
import {
  CarrierSettingsCard,
  carrierSettingsDefault,
  carrierSettingsForm,
  CarrierSettingsValue,
} from "./outbound-labels/carrier-settings-card";
import {
  CompetitiveSpreadSettingsCard,
  competitiveSpreadSettingsDefault,
  competitiveSpreadSettingsForm,
  redoCompetitiveSpreadSettingsDefault,
  redoCompetitiveSpreadSettingsForm,
} from "./outbound-labels/competitive-spread-settings";
import { ConnectTeamTo3plCard } from "./outbound-labels/connect-team-to-3pl";
import { CopyFulfillmentGroupViewsCard } from "./outbound-labels/copy-fulfillment-group-views-card";
import {
  CreditCardFeeSettingsCard,
  creditCardFeeSettingsDefault,
  creditCardFeeSettingsForm,
} from "./outbound-labels/credit-card-fee-settings";
import { DynamicRatePricingSettingsCard } from "./outbound-labels/dynamic-rate-pricing-settings";
import {
  createDynamicRatePricingSettingsFormValue,
  dynamicRatePricingSettingsDefault,
  dynamicRatePricingSettingsForm,
  getUpdatedDynamicRatePricingSettings,
} from "./outbound-labels/dynamic-rate-pricing-settings-form";
import {
  buildOmsSubscriptionBillingUpdate,
  buildOutboundLabelsFeesUpdate,
  buildWmsFeesUpdate,
  buildWmsSubscriptionBillingUpdate,
} from "./outbound-labels/fee-settings-merge";
import {
  FreightSettingsCard,
  freightSettingsDefault,
  freightSettingsForm,
} from "./outbound-labels/freight-settings-card";
import { OutboundLabelsGeneral } from "./outbound-labels/general";
import { ImportPackageLearningCsvCard } from "./outbound-labels/import-package-learning-csv-card";
import { ImportVariantDimensionsCsvCard } from "./outbound-labels/import-variant-dimensions-csv-card";
import {
  NetTermsSettings,
  netTermsSettingsDefaultValue,
  netTermsSettingsForm,
  netTermsValueFromSettings,
} from "./outbound-labels/net-terms-settings";
import { RecognizeOrdersCard } from "./outbound-labels/recognize-orders-card";
import {
  canChangeOutboundLabelsBalance,
  omsEngineers,
  omsEngineersBase,
} from "./outbound-labels/user-access";

function cloneOutboundLabels(value: unknown): unknown {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

import { TeamType } from "@redotech/account-common/team-type";
import { BigcommerceOrderCountsCard } from "./outbound-labels/bigcommerce-order-counts-card";
import { LookupFulfillmentBreakdownCard } from "./outbound-labels/lookup-fulfillment-breakdown";
import { LookupOrderTimelineCard } from "./outbound-labels/lookup-order-timeline";
import { LookupRateDetailsCard } from "./outbound-labels/lookup-rate-details";
import {
  MerchantCarrierFeeInitialState,
  MerchantCarrierFeeSettingsCard,
  merchantCarrierFeeSettingsDefault,
  merchantCarrierFeeSettingsForm,
} from "./outbound-labels/merchant-carrier-fee-settings";
import { NetsuiteIntegrationCard } from "./outbound-labels/netsuite-integration";
import { RateCalculatorCard } from "./outbound-labels/oms-pricing-tool/oms-pricing-tool-card";
import {
  createPersonalizedSlipsMonetizationFormValue,
  getUpdatedPersonalizedSlipsMonetization,
  personalizedSlipsMonetizationDefault,
  personalizedSlipsMonetizationForm,
  PersonalizedSlipsSettingsCard,
} from "./outbound-labels/personalized-slips-settings";
import {
  PointsProgramSettingsCard,
  pointsSettingsDefault,
  pointsSettingsForm,
} from "./outbound-labels/points-program-settings";
import {
  ProductSyncFieldsCard,
  productSyncFieldsDefault,
  productSyncFieldsForm,
} from "./outbound-labels/product-sync-fields-card";
import { OutboundLabelRateCardCard } from "./outbound-labels/rate-tables/rate-table-card";
import { RedoChildCarrierAccountsCard } from "./outbound-labels/redo-child-carrier-accounts";
import { RedoLandmarkGlobalIntegration } from "./outbound-labels/redo-landmark-global-integration";
import {
  paymentTermsDefault,
  paymentTermsInput,
  pullForwardWindowDaysDefault,
  pullForwardWindowDaysInput,
  suppliesEnabledDefault,
  suppliesEnabledInput,
  SuppliesSettingsCard,
} from "./outbound-labels/supplies-card";
import {
  WmsEnabledCard,
  wmsEnabledDefault,
  wmsEnabledForm,
} from "./outbound-labels/wms-enabled-card";

export const OutboundLabelsPage = memo(function OutboundLabelsPage() {
  const params = useParams();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);

  const handleSave = useHandler(() => {
    loadTeam();
  });

  const settingsLoad = useLoad<OutboundLabelValue | undefined>(async () => {
    if (!team) {
      return;
    }

    const carrierSettings: CarrierSettingsValue = {
      manifestBeforeCutoff:
        team.settings.outboundLabels?.manifestBeforeCutoff ?? false,
      directToCarrierIntegrations: {
        ups:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.ups ?? true,
        fedex:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.fedex ?? false,
        usps:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.usps ?? true,
        dhl:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.dhl ?? false,
        dhlInternational:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.dhlInternational ?? false,
        canadaPost:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.canadaPost ?? false,
        wegrow:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.wegrow ?? false,
        royalMail:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.royalMail ?? false,
      },
      internationalBridge: {
        usps:
          team.settings.outboundLabels?.enabledCarrierAccounts
            ?.internationalBridge?.usps ?? true,
      },
      blockedServiceLevels:
        team.settings.outboundLabels?.blockedServiceLevels ?? [],
      dhlDirectIntegrationSettings: {
        pickupId:
          team.settings.outboundLabels?.dhlDirectIntegrationSettings
            ?.pickupId ?? "",
        distributionCenter:
          team.settings.outboundLabels?.dhlDirectIntegrationSettings
            ?.distributionCenter ?? "",
        useDefaultForAllLocations:
          team.settings.outboundLabels?.dhlDirectIntegrationSettings
            ?.useDefaultForAllLocations ?? false,
        useDHLLCG:
          team.settings.outboundLabels?.dhlDirectIntegrationSettings
            ?.useDHLLCG ?? false,
      },
      uniUni: {
        enabled: team.settings.outboundLabels?.uniUni?.enabled ?? false,
        pickupWarehouseId:
          team.settings.outboundLabels?.uniUni?.pickupWarehouseId ?? null,
        customerId: team.settings.outboundLabels?.uniUni?.customerId ?? "",
      },
      useAustraliaPostCarrierAccount:
        team.settings.outboundLabels?.useAustraliaPostCarrierAccount ?? false,
      amazonBuyShippingAllowExternalOrders:
        team.settings.outboundLabels?.amazonBuyShippingAllowExternalOrders ===
        true,
      filterOneCentRates:
        team.settings.outboundLabels?.filterOneCentRates ?? false,
      useEpostGlobalCarrierAccount:
        team.settings.outboundLabels?.useEpostGlobalCarrierAccount ?? false,
      useApcCarrierAccount:
        team.settings.outboundLabels?.useApcCarrierAccount ?? false,
      useDhlExpressCarrierAccount:
        team.settings.outboundLabels?.useDhlExpressCarrierAccount ?? false,
      apcFulfillmentCenter:
        team.settings.outboundLabels?.apcFulfillmentCenter ?? null,
      epgFulfillmentCenter:
        team.settings.outboundLabels?.epgFulfillmentCenter ?? null,
    };

    return {
      ...outboundLabelDefault,
      newOmsEnabled: team.settings.outboundLabels?.newOmsEnabled ?? false,
      disableShopifySync:
        team.settings.outboundLabels?.disableShopifySync === true,
      blockedSalesChannels:
        team.settings.outboundLabels?.blockedSalesChannels ?? [],
      showRedoLogoOnLabel:
        team.settings.outboundLabels?.showRedoLogoOnLabel ?? true,
      personalizedSlipsEnabled:
        team.settings.outboundLabels?.personalizedSlipsEnabled ?? false,
      personalizedSlipsMonetization:
        createPersonalizedSlipsMonetizationFormValue(
          team.settings.outboundLabels?.personalizedSlipsMonetization,
        ),
      automationDelaySeconds: (
        team.settings.outboundLabels?.automationDelaySeconds ?? 0
      ).toFixed(0),
      binNamespace: team.settings.outboundLabels?.binNamespace ?? "",
      binKey: team.settings.outboundLabels?.binKey ?? "",
      carrierSettings,
      balanceSettings: {
        minimumBalance:
          team?.settings?.outboundLabels?.billing?.autoReload?.floor ?? 0,
      },
      netTermsSettings: netTermsValueFromSettings(
        team?.settings?.outboundLabels?.billing?.netTerms,
      ),
      dynamicRatePricingSettings: createDynamicRatePricingSettingsFormValue(
        team.settings.outboundLabels?.dynamicRatePricingSettings,
      ),
      competitiveSpreadSettings: {
        enabled:
          team.settings.outboundLabels?.competitiveSpreadSettings?.enabled ??
          true,
        beatRateBy:
          team.settings.outboundLabels?.competitiveSpreadSettings?.beatRateBy ??
          DEFAULT_COMP_SPREAD_BEAT_RATE_BY,
        minBeatRateBy:
          team.settings.outboundLabels?.competitiveSpreadSettings
            ?.minBeatRateBy ?? DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY,
        carrierSpreadOverrides:
          team.settings.outboundLabels?.competitiveSpreadSettings?.carrierSpreadOverrides?.map(
            (o) => ({
              carriers: o.carriers,
              minBeatRateBy: o.minBeatRateBy,
              maxBeatRateBy: o.maxBeatRateBy,
            }),
          ) ?? DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES,
        minSpreadFloor:
          team.settings.outboundLabels?.competitiveSpreadSettings
            ?.minSpreadFloor ?? 0,
        enabledCarriers:
          team.settings.outboundLabels?.competitiveSpreadSettings
            ?.enabledCarriers ?? [],
        applyMerchantCompSpreadWhenTied:
          team.settings.outboundLabels?.competitiveSpreadSettings
            ?.applyMerchantCompSpreadWhenTied ?? false,
      },
      redoCompetitiveSpreadSettings: {
        enabled:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings
            ?.enabled ?? true,
        beatRateBy:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings
            ?.beatRateBy ?? DEFAULT_COMP_SPREAD_BEAT_RATE_BY,
        minBeatRateBy:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings
            ?.minBeatRateBy ?? DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY,
        carrierSpreadOverrides:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings?.carrierSpreadOverrides?.map(
            (o) => ({
              carriers: o.carriers,
              minBeatRateBy: o.minBeatRateBy,
              maxBeatRateBy: o.maxBeatRateBy,
            }),
          ) ?? DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES,
        minSpreadDifference:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings
            ?.minSpreadDifference ?? 0,
        enabledCarriers:
          team.settings.outboundLabels?.redoCompetitiveSpreadSettings
            ?.enabledCarriers ?? [],
      },
      pointsSettings: {
        enabled: team.settings.outboundLabels?.pointsSettings?.enabled ?? false,
        uspsLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings
            ?.uspsLabelPointsAwarded ?? 5,
        fedexLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings
            ?.fedexLabelPointsAwarded ?? 5,
        upsLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings?.upsLabelPointsAwarded ??
          5,
        amazonLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings
            ?.amazonLabelPointsAwarded ?? 5,
        firstMileLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings
            ?.firstMileLabelPointsAwarded ?? 5,
        dhlEcommerceLabelPointsAwarded:
          team.settings.outboundLabels?.pointsSettings
            ?.dhlEcommerceLabelPointsAwarded ?? 5,
      },

      fees: {
        merchantCarrierAccountLabelFee: {
          amount:
            team.settings.outboundLabels?.fees?.merchantCarrierAccountLabelFee
              ?.amount ?? 0,
          tiktokShippingAmount:
            team.settings.outboundLabels?.fees?.merchantCarrierAccountLabelFee
              ?.tiktokShippingAmount ?? 0,
        },
        monthlySubscriptionFee: {
          amount:
            team.settings.outboundLabels?.fees?.monthlySubscriptionFee
              ?.amount ?? 0,
          turnedOnAt:
            team.settings.outboundLabels?.fees?.monthlySubscriptionFee
              ?.turnedOnAt ?? undefined,
        },
        wmsSubscriptionFee: {
          amount:
            team.settings.outboundLabels?.wms?.fees?.monthlySubscriptionFee
              ?.amount ?? 0,
          turnedOnAt:
            team.settings.outboundLabels?.wms?.fees?.monthlySubscriptionFee
              ?.turnedOnAt ?? undefined,
        },
      },
      chargeCreditCardProcessingFee:
        team.settings.outboundLabels?.fees?.chargeCreditCardProcessingFee ??
        false,
      wmsEnabled: {
        enabled: team.settings.outboundLabels?.wms?.enabled ?? false,
        packingParcelsEnabled:
          team.settings.outboundLabels?.wms?.packingParcelsEnabled ?? false,
        binCoordinateMonitoringEnabled:
          team.settings.outboundLabels?.wms?.binCoordinateMonitoring?.enabled ??
          false,
        binCoordinateMonitoringAlertThresholdPercent:
          team.settings.outboundLabels?.wms?.binCoordinateMonitoring
            ?.alertThresholdPercent ?? 80,
        sourceOfTruthEnabled:
          team.settings.outboundLabels?.wms?.sourceOfTruthEnabled ?? false,
        sourceOfTruthLocationIds:
          team.settings.outboundLabels?.wms?.sourceOfTruthLocationIds ?? [],
        inferBinFromSku:
          team.settings.outboundLabels?.wms?.inferBinFromSku ?? false,
        processingAutoReceive:
          team.settings.outboundLabels?.wms?.processingAutoReceive ?? false,
        stationProcessingSessionMode:
          team.settings.outboundLabels?.wms?.stationProcessingSessionMode ??
          false,
        stationProcessingSessionWorkstationIds:
          team.settings.outboundLabels?.wms
            ?.stationProcessingSessionWorkstationIds ?? [],
        receiveToBinEnabled:
          team.settings.outboundLabels?.wms?.receiveToBinEnabled ?? false,
        strictLicensePlateScan:
          team.settings.outboundLabels?.wms?.strictLicensePlateScan ?? false,
        strictLicensePlateScanBypassMerchantIds:
          team.settings.outboundLabels?.wms?.strictLicensePlateScanBypassMerchantIds?.map(
            (id) => id.toString(),
          ) ?? [],
        processingWmsEnabled:
          team.settings.outboundLabels?.wms?.processingWmsEnabled ?? false,
        transferDistributionMode:
          team.settings.outboundLabels?.wms?.transferDistributionMode ??
          TransferDistributionMode.BIN_FIRST,
        onboardingMode:
          team.settings.outboundLabels?.wms?.onboardingMode ?? "external",
      },
      productSyncFields: {
        tags:
          team.settings.outboundLabels?.productUpdateSyncFields?.tags ?? false,
        title:
          team.settings.outboundLabels?.productUpdateSyncFields?.title ?? false,
        sku:
          team.settings.outboundLabels?.productUpdateSyncFields?.sku ?? false,
      },
      freightSettings: {
        freightEnabled:
          team.settings.outboundLabels?.freightSettings?.freightEnabled ?? true,
        spreadPercentage:
          team.settings.outboundLabels?.freightSettings?.spreadPercentage ?? 12,
      },
      suppliesEnabled: team.settings.supplies?.enabled ?? false,
      paymentTerms: team.settings.supplies?.paymentTerms,
      pullForwardWindowDays:
        team.settings.supplies?.pullForwardWindowDaysOverride ?? undefined,
    };
  }, [team]);

  return (
    <OutboundLabelsForm
      initialValue={settingsLoad.value}
      key={+!team}
      onSave={handleSave}
      teamId={teamId}
    />
  );
});

export const outboundLabelForm = groupInput({
  newOmsEnabled: input<boolean>(),
  disableShopifySync: input<boolean>(),
  blockedSalesChannels: input<string[]>({ equal: arrayEqual(stringEqual) }),
  showRedoLogoOnLabel: input<boolean>(),
  personalizedSlipsEnabled: input<boolean>(),
  personalizedSlipsMonetization: personalizedSlipsMonetizationForm,
  automationDelaySeconds: input<string>(),
  binNamespace: input<string>(),
  binKey: input<string>(),
  carrierSettings: carrierSettingsForm,
  balanceSettings: balanceSettingsForm,
  netTermsSettings: netTermsSettingsForm,
  dynamicRatePricingSettings: dynamicRatePricingSettingsForm,
  competitiveSpreadSettings: competitiveSpreadSettingsForm,
  redoCompetitiveSpreadSettings: redoCompetitiveSpreadSettingsForm,
  pointsSettings: pointsSettingsForm,
  fees: merchantCarrierFeeSettingsForm,
  chargeCreditCardProcessingFee: creditCardFeeSettingsForm,
  wmsEnabled: wmsEnabledForm,
  productSyncFields: productSyncFieldsForm,
  freightSettings: freightSettingsForm,
  suppliesEnabled: suppliesEnabledInput,
  paymentTerms: paymentTermsInput,
  pullForwardWindowDays: pullForwardWindowDaysInput,
});

export type OutboundLabelForm = InputSchema.Input<typeof outboundLabelForm>;

export type OutboundLabelValue = InputSchema.Value<typeof outboundLabelForm>;

export const outboundLabelDefault: OutboundLabelValue = {
  newOmsEnabled: false,
  disableShopifySync: false,
  blockedSalesChannels: [],
  showRedoLogoOnLabel: true,
  personalizedSlipsEnabled: false,
  personalizedSlipsMonetization: personalizedSlipsMonetizationDefault,
  automationDelaySeconds: "0",
  binNamespace: "",
  binKey: "",
  carrierSettings: carrierSettingsDefault,
  balanceSettings: balanceSettingsDefaultValue,
  netTermsSettings: netTermsSettingsDefaultValue,
  dynamicRatePricingSettings: dynamicRatePricingSettingsDefault,
  competitiveSpreadSettings: competitiveSpreadSettingsDefault,
  redoCompetitiveSpreadSettings: redoCompetitiveSpreadSettingsDefault,
  pointsSettings: pointsSettingsDefault,
  fees: merchantCarrierFeeSettingsDefault,
  chargeCreditCardProcessingFee: creditCardFeeSettingsDefault,
  wmsEnabled: wmsEnabledDefault,
  productSyncFields: productSyncFieldsDefault,
  freightSettings: freightSettingsDefault,
  suppliesEnabled: suppliesEnabledDefault,
  paymentTerms: paymentTermsDefault,
  pullForwardWindowDays: pullForwardWindowDaysDefault,
};

const usersWhoCanEditRateTablesBase = [
  ...omsEngineersBase,
  "suz",
  "camryn",
  "aaron",
  "jackie",
  "ridge",
  "hayden",
  "boston",
  "kyle",
  "claude",
  "jason.broadbent",
  "alexis",
  "seth",
];

export const usersWhoCanEditRateTables = usersWhoCanEditRateTablesBase.flatMap(
  (name) => [`${name}@getredo.com`, `${name}@redo.com`],
);

export const usersWhoCanEditMerchantFeesBase = [
  ...usersWhoCanEditRateTablesBase,
  "spencer",
  "dallin.orchard",
];

export const usersWhoCanEditMerchantFees =
  usersWhoCanEditMerchantFeesBase.flatMap((name) => [
    `${name}@getredo.com`,
    `${name}@redo.com`,
  ]);

const usersWhoCanSeeRateTablesBase = [
  ...usersWhoCanEditRateTablesBase,
  "patrick",
  "jordan",
  "hayden",
  "spencer",
  "logan",
  "eric",
  "boston",
  "dominic",
  "bryce",
];

const usersWhoCanSeeRateTables = usersWhoCanSeeRateTablesBase.flatMap(
  (name) => [`${name}@getredo.com`, `${name}@redo.com`],
);

const usersWhoCanTurnOffCreditCardFeeBase = [
  ...omsEngineersBase,
  "spencer",
  "jackie",
  "hayden",
  "suz",
  "dallin.orchard",
  "boston",
];

const usersWhoCanTurnOffCreditCardFee =
  usersWhoCanTurnOffCreditCardFeeBase.flatMap((name) => [
    `${name}@getredo.com`,
    `${name}@redo.com`,
  ]);

const usersWhoCanEditCompSpreadRangesBase = [
  ...omsEngineersBase,
  "suz",
  "jackie",
  "hayden",
];

export const usersWhoCanEditCompSpreadRanges =
  usersWhoCanEditCompSpreadRangesBase.flatMap((name) => [
    `${name}@getredo.com`,
    `${name}@redo.com`,
  ]);

const OutboundLabelsForm = memo(function OutboundLabelsForm({
  onSave,
  teamId,
  initialValue,
}: {
  teamId: string;
  initialValue: OutboundLabelValue | undefined;
  onSave?: (value: OutboundLabelValue) => void;
}) {
  const client = useRequiredContext(RedoAdminClientContext);
  const input = useInput(
    outboundLabelForm,
    initialValue || outboundLabelDefault,
  );
  const auth = useRequiredContext(AuthContext);
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const userCanUseBalance = useMemo(() => {
    return canChangeOutboundLabelsBalance(auth.email);
  }, [auth.email]);

  const userCanEditRateTables = useMemo(() => {
    return usersWhoCanEditRateTables.includes(auth.email);
  }, [auth]);

  const userCanSeeRateTables = useMemo(() => {
    return usersWhoCanSeeRateTables.includes(auth.email);
  }, [auth]);

  const userCanTurnOffCreditCardFee = usersWhoCanTurnOffCreditCardFee.includes(
    auth.email,
  );

  const userCanEditMerchantFees = useMemo(() => {
    return usersWhoCanEditMerchantFees.includes(auth.email);
  }, [auth]);

  const userCanEditCompSpreadRanges = useMemo(() => {
    return usersWhoCanEditCompSpreadRanges.includes(auth.email);
  }, [auth]);

  const isOmsEngineer = useMemo(() => {
    return omsEngineers.includes(auth.email);
  }, [auth]);

  const merchantFeeInitialState: MerchantCarrierFeeInitialState = useMemo(
    () => ({
      omsCanBeEnabled:
        !initialValue?.fees.monthlySubscriptionFee.turnedOnAt ||
        initialValue?.fees.monthlySubscriptionFee.amount <= 0,
      wmsCanBeEnabled:
        !initialValue?.fees.wmsSubscriptionFee.turnedOnAt ||
        initialValue?.fees.wmsSubscriptionFee.amount <= 0,
      initialOmsFee: initialValue?.fees.monthlySubscriptionFee.amount ?? 0,
      initialWmsFee: initialValue?.fees.wmsSubscriptionFee.amount ?? 0,
      initialLabelFee:
        initialValue?.fees.merchantCarrierAccountLabelFee.amount ?? 0,
      initialTiktokLabelFee:
        initialValue?.fees.merchantCarrierAccountLabelFee
          .tiktokShippingAmount ?? 0,
      initialOmsTurnedOnAt:
        initialValue?.fees.monthlySubscriptionFee.turnedOnAt,
      initialWmsTurnedOnAt: initialValue?.fees.wmsSubscriptionFee.turnedOnAt,
    }),
    [initialValue],
  );

  const initialSotEnabled =
    initialValue?.wmsEnabled.sourceOfTruthEnabled ?? false;
  const [showWmsOnboardConfirmation, setShowWmsOnboardConfirmation] =
    useState(false);
  const pendingSaveSignal = useRef<AbortSignal | null>(null);

  const [
    redoLandmarkGlobalConnectionState,
    setRedoLandmarkGlobalConnectionState,
  ] = useState<ChannelConnectionState>(ChannelConnectionState.NOT_CONNECTED);

  useLoad(
    async (signal) => {
      const response = await rpcClient.getRedoLandmarkGlobalIntegration(
        { teamId },
        { signal },
      );
      setRedoLandmarkGlobalConnectionState(
        response.exists
          ? ChannelConnectionState.CONNECTED
          : ChannelConnectionState.NOT_CONNECTED,
      );
      return response;
    },
    [teamId, rpcClient],
  );

  useEffect(() => {
    if (initialValue) {
      input.setValue(initialValue);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  const wmsSotToggled =
    !initialSotEnabled && input.value.wmsEnabled.sourceOfTruthEnabled;

  const doSave: PageFormSave = useHandler(async (signal) => {
    const team = await getTeam(client, { teamId, signal });
    const previousOutboundLabels = cloneOutboundLabels(
      team.settings?.outboundLabels,
    );
    const value = input.value;

    team.settings.outboundLabels = {
      ...team.settings.outboundLabels,
      newOmsEnabled: value?.newOmsEnabled,
      disableShopifySync: value.disableShopifySync,
      // Integrations add and remove channels on connect, so keep the stored list unless edited here.
      ...(input.inputs.blockedSalesChannels.changed && {
        blockedSalesChannels: value.blockedSalesChannels,
      }),
      showRedoLogoOnLabel: value?.showRedoLogoOnLabel,
      personalizedSlipsEnabled: value?.personalizedSlipsEnabled,
      personalizedSlipsMonetization: getUpdatedPersonalizedSlipsMonetization(
        value.personalizedSlipsMonetization,
      ),
      useAustraliaPostCarrierAccount:
        value?.carrierSettings.useAustraliaPostCarrierAccount,
      useEpostGlobalCarrierAccount:
        value?.carrierSettings.useEpostGlobalCarrierAccount,
      useApcCarrierAccount: value?.carrierSettings.useApcCarrierAccount,
      useDhlExpressCarrierAccount:
        value?.carrierSettings.useDhlExpressCarrierAccount,
      apcFulfillmentCenter:
        value?.carrierSettings.apcFulfillmentCenter ?? undefined,
      epgFulfillmentCenter:
        value?.carrierSettings.epgFulfillmentCenter ?? undefined,
      automationDelaySeconds: Math.floor(
        parseFloat(value.automationDelaySeconds) || 0,
      ),
      binNamespace: value.binNamespace,
      binKey: value.binKey,
      blockedCarrierAccounts:
        team.settings.outboundLabels?.blockedCarrierAccounts ?? [],
      blockedServiceLevels: value.carrierSettings.blockedServiceLevels,
      manifestBeforeCutoff: value.carrierSettings.manifestBeforeCutoff,
      wms: {
        ...team.settings.outboundLabels?.wms,
        enabled: value.wmsEnabled.enabled,
        packingParcelsEnabled: value.wmsEnabled.packingParcelsEnabled,
        sourceOfTruthEnabled: value.wmsEnabled.sourceOfTruthEnabled,
        sourceOfTruthLocationIds: value.wmsEnabled.sourceOfTruthLocationIds,
        inferBinFromSku: value.wmsEnabled.inferBinFromSku,
        processingAutoReceive: value.wmsEnabled.processingAutoReceive,
        stationProcessingSessionMode:
          value.wmsEnabled.stationProcessingSessionMode,
        stationProcessingSessionWorkstationIds:
          value.wmsEnabled.stationProcessingSessionWorkstationIds,
        receiveToBinEnabled: value.wmsEnabled.receiveToBinEnabled,
        strictLicensePlateScan: value.wmsEnabled.strictLicensePlateScan,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        strictLicensePlateScanBypassMerchantIds: value.wmsEnabled
          .strictLicensePlateScanBypassMerchantIds as any,
        processingWmsEnabled: value.wmsEnabled.processingWmsEnabled,
        transferDistributionMode: value.wmsEnabled.transferDistributionMode,
        onboardingMode: value.wmsEnabled.onboardingMode,
        pickSession:
          team.settings.outboundLabels?.wms?.pickSession ?? undefined,
        fees: buildWmsFeesUpdate(
          team.settings.outboundLabels?.wms?.fees,
          value.fees.wmsSubscriptionFee,
        ),
        subscriptionBilling: buildWmsSubscriptionBillingUpdate(
          team.settings.outboundLabels?.wms?.subscriptionBilling,
          team.settings.outboundLabels?.wms?.fees,
          value.fees.wmsSubscriptionFee,
        ),
        binCoordinateMonitoring: {
          enabled: value.wmsEnabled.binCoordinateMonitoringEnabled,
          alertThresholdPercent:
            value.wmsEnabled.binCoordinateMonitoringAlertThresholdPercent,
        },
      },
      amazonBuyShippingAllowExternalOrders:
        value.carrierSettings.amazonBuyShippingAllowExternalOrders,
      filterOneCentRates: value.carrierSettings.filterOneCentRates,
      enabledCarrierAccounts: {
        ...team.settings.outboundLabels?.enabledCarrierAccounts,
        directToCarrierIntegrations: {
          ...team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations,
          ups: value.carrierSettings.directToCarrierIntegrations.ups,
          fedex: value.carrierSettings.directToCarrierIntegrations.fedex,
          usps: value.carrierSettings.directToCarrierIntegrations.usps,
          dhl: value.carrierSettings.directToCarrierIntegrations.dhl,
          dhlInternational:
            value.carrierSettings.directToCarrierIntegrations.dhlInternational,
          canadaPost:
            value.carrierSettings.directToCarrierIntegrations.canadaPost,
          wegrow: value.carrierSettings.directToCarrierIntegrations.wegrow,
          royalMail:
            value.carrierSettings.directToCarrierIntegrations.royalMail,
        },
        internationalBridge: {
          ...team.settings.outboundLabels?.enabledCarrierAccounts
            ?.internationalBridge,
          usps: value.carrierSettings.internationalBridge.usps,
        },
      },
      dhlDirectIntegrationSettings: {
        pickupId: value.carrierSettings.dhlDirectIntegrationSettings.pickupId,
        distributionCenter:
          value.carrierSettings.dhlDirectIntegrationSettings.distributionCenter,
        useDefaultForAllLocations:
          value.carrierSettings.dhlDirectIntegrationSettings
            .useDefaultForAllLocations,
        useDHLLCG: value.carrierSettings.dhlDirectIntegrationSettings.useDHLLCG,
      },
      uniUni: {
        enabled: value.carrierSettings.uniUni.enabled,
        pickupWarehouseId:
          value.carrierSettings.uniUni.pickupWarehouseId ?? undefined,
        customerId: value.carrierSettings.uniUni.customerId,
      },
      billing: {
        ...team.settings.outboundLabels?.billing,
        autoReload: {
          amount:
            team?.settings?.outboundLabels?.billing?.autoReload?.amount ?? 0,
          threshold:
            team?.settings?.outboundLabels?.billing?.autoReload?.threshold ?? 0,
          floor: value.balanceSettings.minimumBalance,
        },
        netTerms: {
          ...value.netTermsSettings,
          enabled:
            team.settings.outboundLabels?.billing?.netTerms?.enabled ?? false,
        },
      },
      dynamicRatePricingSettings: getUpdatedDynamicRatePricingSettings(
        value.dynamicRatePricingSettings,
        team.settings.outboundLabels?.dynamicRatePricingSettings,
      ),
      competitiveSpreadSettings: {
        enabled: value.competitiveSpreadSettings.enabled,
        beatRateBy: Number(
          value.competitiveSpreadSettings.beatRateBy.toFixed(2),
        ),
        minBeatRateBy: Number(
          value.competitiveSpreadSettings.minBeatRateBy.toFixed(2),
        ),
        carrierSpreadOverrides:
          value.competitiveSpreadSettings.carrierSpreadOverrides.map((o) => ({
            carriers: o.carriers,
            minBeatRateBy: Number(o.minBeatRateBy.toFixed(2)),
            maxBeatRateBy: Number(o.maxBeatRateBy.toFixed(2)),
          })),
        minSpreadFloor: Number(
          value.competitiveSpreadSettings.minSpreadFloor.toFixed(2),
        ),
        enabledCarriers: value.competitiveSpreadSettings.enabledCarriers,
        applyMerchantCompSpreadWhenTied:
          value.competitiveSpreadSettings.applyMerchantCompSpreadWhenTied,
      },
      redoCompetitiveSpreadSettings: {
        enabled: value.redoCompetitiveSpreadSettings.enabled,
        beatRateBy: Number(
          value.redoCompetitiveSpreadSettings.beatRateBy.toFixed(2),
        ),
        minBeatRateBy: Number(
          value.redoCompetitiveSpreadSettings.minBeatRateBy.toFixed(2),
        ),
        carrierSpreadOverrides:
          value.redoCompetitiveSpreadSettings.carrierSpreadOverrides.map(
            (o) => ({
              carriers: o.carriers,
              minBeatRateBy: Number(o.minBeatRateBy.toFixed(2)),
              maxBeatRateBy: Number(o.maxBeatRateBy.toFixed(2)),
            }),
          ),
        minSpreadDifference: Number(
          value.redoCompetitiveSpreadSettings.minSpreadDifference.toFixed(2),
        ),
        enabledCarriers: value.redoCompetitiveSpreadSettings.enabledCarriers,
      },
      pointsSettings: {
        enabled: value.pointsSettings.enabled,
        uspsLabelPointsAwarded: value.pointsSettings.uspsLabelPointsAwarded,
        fedexLabelPointsAwarded: value.pointsSettings.fedexLabelPointsAwarded,
        upsLabelPointsAwarded: value.pointsSettings.upsLabelPointsAwarded,
        amazonLabelPointsAwarded: value.pointsSettings.amazonLabelPointsAwarded,
        firstMileLabelPointsAwarded:
          value.pointsSettings.firstMileLabelPointsAwarded,
        dhlEcommerceLabelPointsAwarded:
          value.pointsSettings.dhlEcommerceLabelPointsAwarded,
      },
      fees: buildOutboundLabelsFeesUpdate(
        team.settings.outboundLabels?.fees,
        value.fees,
        value.chargeCreditCardProcessingFee,
      ),
      subscriptionBilling: buildOmsSubscriptionBillingUpdate(
        team.settings.outboundLabels?.subscriptionBilling,
        team.settings.outboundLabels?.fees,
        value.fees,
      ),
      productUpdateSyncFields: {
        tags: value.productSyncFields.tags,
        title: value.productSyncFields.title,
        sku: value.productSyncFields.sku,
      },
      freightSettings: {
        freightEnabled: value.freightSettings.freightEnabled,
        spreadPercentage: value.freightSettings.spreadPercentage,
      },
      showSuppliesStorefront: value.suppliesEnabled,
    };

    // These live on settings.supplies rather than settings.outboundLabels.
    team.settings.supplies = {
      ...team.settings.supplies,
      // Source of truth for reads. Keep writing the legacy
      // outboundLabels.showSuppliesStorefront above until it is dropped.
      enabled: value.suppliesEnabled,
      paymentTerms: value.paymentTerms,
      pullForwardWindowDaysOverride: value.pullForwardWindowDays,
    };

    // Disable carrier service level learning if competitive spread is enabled
    // as it interferes with selecting the adjusted rate
    if (
      value.competitiveSpreadSettings.enabled ||
      value.redoCompetitiveSpreadSettings.enabled
    ) {
      team.settings.outboundLabels.learnedPresets = {
        parcel: team.settings.outboundLabels.learnedPresets?.parcel ?? true,
        weight: team.settings.outboundLabels.learnedPresets?.weight ?? false,
        ["carrier-service-level"]: false,
      };
    }

    await updateTeam(client, { teamId, team, signal });
    if (
      shouldTriggerRateRefresh(
        { settings: { outboundLabels: previousOutboundLabels } },
        team,
      )
    ) {
      notifyAdminRateRefreshNeeded(teamId);
    }

    await rpcClient.createDefaultFlowsAndTemplates(
      { teamId, category: "Oms" },
      { signal },
    );

    onSave?.(value);
  });

  const save: PageFormSave = useHandler(async (signal) => {
    if (wmsSotToggled) {
      pendingSaveSignal.current = signal;
      setShowWmsOnboardConfirmation(true);
      return;
    }
    await doSave(signal);
  });

  const isFulfillmentService = useMemo(() => {
    return team.teamType === TeamType.FulfillmentService;
  }, [team]);

  return (
    <div className={teamCss.container}>
      <PageForm
        initial={initialValue || outboundLabelDefault}
        input={input}
        save={save}
      >
        <div className={gridCss.grid}>
          <div className={gridCss.span12}>
            {userCanSeeRateTables && (
              <>
                <div className={gridCss.span12}>
                  <OutboundLabelRateCardCard
                    readonly={!userCanEditRateTables}
                  />
                </div>
                <br />
                <div className={gridCss.span12}>
                  <RateCalculatorCard />
                </div>
              </>
            )}
          </div>
        </div>
        <br />
        <div className={gridCss.grid}>
          <div className={gridCss.span4}>
            <OutboundLabelsGeneral input={input} team={team} />
            <br />
            <RecognizeOrdersCard
              enabled={team.settings.recognize?.enabled === true}
              teamId={teamId}
            />
            <br />
            <PersonalizedSlipsSettingsCard
              canEditMonetization={userCanEditMerchantFees}
              input={input}
            />
            {userCanUseBalance && (
              <>
                <br />
                <AddBalanceCreditCard team={team} />
              </>
            )}
            <br />
            <BalanceSettings
              balanceSettingsInput={input.inputs.balanceSettings}
              team={team}
            />
            {userCanUseBalance && (
              <>
                <br />
                <NetTermsSettings
                  enabled={
                    team?.settings?.outboundLabels?.billing?.netTerms
                      ?.enabled ?? false
                  }
                  netTermsInput={input.inputs.netTermsSettings}
                  team={team}
                />
              </>
            )}
            <br />
            <MerchantCarrierFeeSettingsCard
              initialState={merchantFeeInitialState}
              input={input}
            />
            <br />
            <CreditCardFeeSettingsCard
              canTurnOffCreditCardFee={userCanTurnOffCreditCardFee}
              input={input}
            />
            <br />
            <RedoLandmarkGlobalIntegration
              connectionState={redoLandmarkGlobalConnectionState}
              setConnectionState={setRedoLandmarkGlobalConnectionState}
            />
            <br />
            <ProductSyncFieldsCard input={input} />
            <br />
            <FreightSettingsCard input={input} isOmsEngineer={isOmsEngineer} />
          </div>
          <div className={gridCss.span8}>
            <div className={gridCss.grid}>
              <div className={gridCss.span6}>
                <LookupOrderTimelineCard />
              </div>
              <div className={gridCss.span6}>
                <LookupFulfillmentBreakdownCard />
              </div>
              <div className={gridCss.span6}>
                <LookupRateDetailsCard />
              </div>
              {isFulfillmentService && (
                <div className={gridCss.span6}>
                  <ConnectTeamTo3plCard />
                </div>
              )}
              <div className={gridCss.span6}>
                <CopyFulfillmentGroupViewsCard />
              </div>
            </div>
            <br />
            <div className={gridCss.span4}>
              <CarrierSettingsCard
                input={input}
                omsEngineers={omsEngineers}
                teamId={teamId}
              />
            </div>
            <br />
            <RedoChildCarrierAccountsCard />
            <br />
            <div className={gridCss.grid}>
              <div className={gridCss.span6}>
                <DynamicRatePricingSettingsCard
                  input={input.inputs.dynamicRatePricingSettings}
                />
                <br />
                <CompetitiveSpreadSettingsCard
                  canEditSpreadRanges={userCanEditCompSpreadRanges}
                  input={input}
                />
              </div>
              <div className={gridCss.span6}>
                <PointsProgramSettingsCard input={input} />
                <br />
                <WmsEnabledCard
                  input={input}
                  isOmsEngineer={isOmsEngineer}
                  workstations={
                    team.settings.outboundLabels?.workstations ?? []
                  }
                />
                <br />
                <SuppliesSettingsCard input={input} />
                <br />
                <ImportPackageLearningCsvCard teamId={teamId} />
                <br />
                <ImportVariantDimensionsCsvCard teamId={teamId} />
                <br />
                <BigcommerceOrderCountsCard teamId={teamId} />
              </div>
            </div>
            <br />
            <NetsuiteIntegrationCard />
          </div>
        </div>
      </PageForm>

      <RedoModal
        isOpen={showWmsOnboardConfirmation}
        modalSize="sm"
        onModalCloseRequested={() => {
          setShowWmsOnboardConfirmation(false);
          pendingSaveSignal.current = null;
        }}
        primaryButton={{
          text: "Yes, enable",
          onClick: async () => {
            setShowWmsOnboardConfirmation(false);
            await doSave(
              pendingSaveSignal.current ?? new AbortController().signal,
            );
            pendingSaveSignal.current = null;
          },
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setShowWmsOnboardConfirmation(false);
            pendingSaveSignal.current = null;
          },
        }}
        title="Enable WMS source of truth?"
      >
        <Text fontSize="sm">
          This will turn on WMS Source of Truth for this team. Ensure that bin
          locations are created, warehouse maps exist, and inventory levels
          exist for products with existing stock. To get inventory levels,
          either import them from their old WMS with a script, or run the
          onboardWms Temporal workflow. This workflow will utilize incremental
          onboarding to allow uncounted inventory levels. Only turn on WMS
          Source of Truth if you know what you are doing.
        </Text>
      </RedoModal>
    </div>
  );
});
